/**
 * @author jakubmaslon
 * @since 2020-11-10
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { MODALS } from "@typings/modals";

import { Heading, Tooltip } from "@ui/Atoms";
import { InfoMessage } from "@ui/Molecules";
import theme from "@ui/themes/default";
import { rem, rgba } from "@ui/helpers";
import transition from "@ui/helpers/transition";
import { Check } from "@ui/Assets/Symbolicons/Check";
import { Info } from "@ui/Assets/Symbolicons/Info";
import { CheckMark } from "@ui/Assets/Symbolicons/CheckMark";

import slugifyFacade from "@services/SlugifyFacade";

import { Markdown } from "@containers/Markdown";

import { useBreakpoint } from "@hooks/useBreakpoint";

interface Benefit {
  label: string;
  tooltip: string;
}

interface Props {
  title: string;
  priceWCurrency: string;
  priceExplanation: string;
  description: string;
  benefits: Benefit[];
  selectBtnLabel: string;
  selectBtnLabelMobile: string;
  moreDetails?: string;
  showModal?: (id: string) => void;
  topLabel?: string;
  isActive: boolean;
  setActivePlan: () => void;
}

interface PlanDetailsProps extends Props {
  isMobile: boolean;
}

const PlanDetails = (props: PlanDetailsProps) => (
  <PlanWrapper isActive={props.isActive}>
    {props.topLabel && <TopLabel>{props.topLabel}</TopLabel>}
    <Title type="h5">{props.title}</Title>
    <PriceWCurrency type="h4">{props.priceWCurrency}</PriceWCurrency>
    <PriceExplanation>{props.priceExplanation}</PriceExplanation>
    <Description>
      <Markdown>{props.description}</Markdown>
    </Description>
    {props.moreDetails && (
      <MoreDetails>
        <InfoMessage
          message={
            <StyledLink
              href="#"
              role="button"
              onClick={e => {
                e.preventDefault();
                props.showModal &&
                  props.showModal(MODALS.BOOKING_SHOW_EMPLOYER_DUTIES);
              }}
            >
              {props.moreDetails}
            </StyledLink>
          }
        />
      </MoreDetails>
    )}
    <Benefits>
      {props.benefits.map(benefit => (
        <Benefit key={slugifyFacade(benefit.label)}>
          <BenefitCheckWLabel>
            <Check variant="line" size={12} />
            <BenefitLabel>{benefit.label}</BenefitLabel>
          </BenefitCheckWLabel>
          {!!benefit.tooltip && (
            <Tooltip
              id={slugifyFacade(benefit.label)}
              placeholder={benefit.tooltip}
            >
              <InfoIconContainer>
                <Info
                  variant="solid"
                  color={theme.palette.blue500}
                  size={18}
                  height={18}
                />
              </InfoIconContainer>
            </Tooltip>
          )}
        </Benefit>
      ))}
    </Benefits>
    {!props.isMobile && (
      <React.Fragment>
        <PriceWCurrencyFooter>{props.priceWCurrency}</PriceWCurrencyFooter>
      </React.Fragment>
    )}
  </PlanWrapper>
);

const Plan = (props: Props) => {
  const { mobile } = useBreakpoint("v1");

  return mobile ? (
    <PlanDetails {...props} isMobile={mobile} />
  ) : (
    <PlanWrapperWCheckMark onClick={props.setActivePlan}>
      <PlanDetails {...props} isMobile={mobile} />
      <CheckMarkWrapper isActive={props.isActive}>
        {props.isActive && (
          <CheckMark variant="solid" color="white" size={16} />
        )}
      </CheckMarkWrapper>
    </PlanWrapperWCheckMark>
  );
};

Plan.displayName = "Plan";

export { Plan };

const PlanWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  padding: ${props => props.theme.margins.base_x2};
  box-shadow: ${props =>
    `0 ${rem(2)} ${rem(4)} 0 ${rgba(props.theme.palette.grey700, 0.2)}`};
  border-radius: ${props => props.theme.buttons.borderRadius};
  border: ${props => (props.isActive ? rem(4) : rem(1))} solid
    ${props => props.theme.palette.blue500};
  background: ${props => props.theme.palette.white};

  ${props => props.theme.breakpoints.tablet} {
    border: none;
    cursor: pointer;

    ${props =>
      props.isActive &&
      css`
        &:after {
          content: "";
          position: absolute;
          bottom: 0;
          height: ${rem(8)};
          width: 100%;
          border-bottom-left-radius: ${props =>
            props.theme.buttons.borderRadius};
          border-bottom-right-radius: ${props =>
            props.theme.buttons.borderRadius};
          background: ${props => props.theme.palette.blue500};
        }
      `}
  }
`;

const CheckMarkWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  position: absolute;
  left: 50%;
  bottom: -${rem(6)};
  height: ${rem(24)};
  width: ${rem(24)};
  transform: translateX(-50%);
  pointer-events: none;
  justify-content: center;
  z-index: 10;
  background: ${props =>
    props.isActive ? props.theme.palette.blue500 : props.theme.palette.white};
  border-radius: 50%;
`;

const PlanWrapperWCheckMark = styled.div`
  position: relative;
  height: 100%;

  &:before {
    content: "";
    display: flex;
    position: absolute;
    left: 50%;
    bottom: -${rem(6)};
    border-radius: 50%;
    box-shadow: ${props =>
      `0 ${rem(2)} ${rem(4)} 0 ${rgba(props.theme.palette.grey700, 0.2)}`};
    height: ${rem(24)};
    width: ${rem(24)};
    transform: translateX(-50%);
    pointer-events: none;
    background: ${props => props.theme.palette.white};
    transition: ${transition(["background", "border", "color"])};
  }

  &:after {
    content: "";
    display: flex;
    position: absolute;
    left: 50%;
    bottom: -${rem(1)};
    height: ${rem(4)};
    width: ${rem(20)};
    transform: translateX(-50%);
    pointer-events: none;
    background: ${props => props.theme.palette.white};
    z-index: 9;
    border-radius: 50%;
    transition: ${transition(["background", "border", "color"])};
  }

  &:hover {
    ${CheckMarkWrapper} {
      &:after {
        content: "";
        position: absolute;
        top: ${rem(3)};
        bottom: ${rem(3)};
        right: ${rem(3)};
        left: ${rem(3)};
        border-radius: 50%;
        border: 1px solid ${props => props.theme.palette.blue500};
      }
    }
  }
`;

const TopLabel = styled.div`
  position: absolute;
  top: 0;
  transform: translateY(-100%);
  width: 100%;
  text-align: center;
  border-top-right-radius: ${props => props.theme.buttons.borderRadius};
  border-top-left-radius: ${props => props.theme.buttons.borderRadius};
  color: ${props => props.theme.palette.white};
  background: ${props => props.theme.palette.blue900};
`;

const Title = styled(Heading)`
  margin-bottom: ${props => props.theme.margins.base_x4};
  color: ${props => props.theme.palette.grey800};
`;

const PriceWCurrency = styled(Heading)`
  margin-bottom: ${props => props.theme.margins.base};
  font-weight: ${props => props.theme.fonts.weights.bold};
  color: ${props => props.theme.palette.grey900};
`;

const PriceExplanation = styled.div`
  margin-bottom: ${props => props.theme.margins.base_x4};
  font-size: ${props => props.theme.fonts.sizes.caption};
  font-weight: ${props => props.theme.fonts.weights.light};
  color: ${props => props.theme.palette.grey400};
`;

const SelectBtnLabel = styled.div`
  margin: auto 0 ${props => props.theme.margins.half};
  font-weight: ${props => props.theme.fonts.weights.bold};
  color: ${props => props.theme.palette.blue500};
`;

const PriceWCurrencyFooter = styled.div`
  margin-bottom: ${props => props.theme.margins.base};
  color: ${props => props.theme.palette.blue500};
`;

const Description = styled.div`
  width: 100%;
  margin-bottom: ${props => props.theme.margins.base_x4};
  font-size: ${props => props.theme.fonts.sizes.caption};
`;

const MoreDetails = styled.div`
  align-self: flex-start;
  margin-bottom: ${props => props.theme.margins.base_x4};
`;

const StyledLink = styled.a`
  font-size: ${props => props.theme.fonts.sizes.body2};
`;

const BenefitCheckWLabel = styled.div`
  display: flex;
  align-items: center;
`;

const BenefitLabel = styled.span`
  margin-left: ${props => props.theme.margins.base_x2};
`;

const Benefit = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: ${props => props.theme.margins.base_x3};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Benefits = styled.div`
  width: 100%;
  margin-bottom: ${props => props.theme.margins.base_x4};
  font-size: ${props => props.theme.fonts.sizes.caption};
`;

const InfoIconContainer = styled.div`
  display: inline-flex;
  margin-left: ${props => props.theme.margins.base};
  cursor: help;
  height: ${rem(14)};
`;
